import { useState, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { Languages, Plus, Trash2, Check, X, AlertTriangle, Search, ChevronLeft, ChevronRight, Eye, EyeOff, ChevronDown, Pencil, Save, Loader2, Globe } from "lucide-react";
import { DEFAULT_LOCALE, EMPTY_LOCALES, PLH_CODE, PLH_LANG_NAME, PLH_FLAG, PLH_SEARCH_LOCALE, TIP_EDIT, TIP_TRANSLATE, TIP_REMOVE, TIP_UNPUBLISH, TIP_PUBLISH, TRANSLATE_MESSAGES } from "../../lib/constants";
import { LOCALE_FILTERS as FILTERS } from "../../lib/tabs";
import { useAuth } from "../../contexts/AuthContext";
import { logger } from "../../lib/logger";
import { clearLocalesCache, checkLocale } from "../../lib/api";

const TOKEN = process.env.REACT_APP_ADMIN_TOKEN || "";
const H = { "Content-Type": "application/json", "X-Admin-Token": TOKEN };

async function apiGet(params = {}) {
  const qs = new URLSearchParams(params).toString();
  const r = await fetch(`/api/admin/locales${qs ? "?"+qs : ""}`, { headers: H });
  if (!r.ok) { const e = await r.json().catch(() => ({ error: r.statusText })); throw new Error(e.error || "Erro na requisição"); }
  return r.json();
}
async function apiPost(body) {
  const r = await fetch("/api/admin/locales", { method: "POST", headers: H, body: JSON.stringify(body) });
  if (!r.ok) { const e = await r.json().catch(() => ({ error: r.statusText })); throw new Error(e.error || "Erro na requisição"); }
  return r.json();
}
async function apiPatch(body) {
  const r = await fetch("/api/admin/locales", { method: "PATCH", headers: H, body: JSON.stringify(body) });
  if (!r.ok) { const e = await r.json().catch(() => ({ error: r.statusText })); throw new Error(e.error || "Erro na requisição"); }
  return r.json();
}

const PAGE_SIZES = [5, 10, 20, 50];

export default function AdminLocalesEditor({ onChange }) {
  const { user } = useAuth();
  const [allLocales, setAllLocales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCode, setNewCode] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newFlag, setNewFlag] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filterOpen, setFilterOpen] = useState(false);
  const [editingUid, setEditingUid] = useState(null);
  const [editForm, setEditForm] = useState({ code: "", label: "", flag: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [translateModal, setTranslateModal] = useState(null);
  const [translateSource, setTranslateSource] = useState("");
  const [translating, setTranslating] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [translateStep, setTranslateStep] = useState("select");
  const [expandedTable, setExpandedTable] = useState(null);
  const [diffData, setDiffData] = useState(null);
  const [selectedUids, setSelectedUids] = useState({});

  async function load() {
    setLoading(true);
    try {
      const data = await apiGet({ order: "sort_order ASC" });
      setAllLocales(Array.isArray(data) ? data.filter(l => !l.deleted_at) : []);
    } catch (err) {
      logger.error(err);
      toast.error("Erro ao carregar idiomas");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  useEffect(() => { setPage(1); }, [search, filter, pageSize]);

  const filtered = useMemo(() => {
    let f = allLocales;
    if (filter === "published") f = f.filter(l => l.published === 1);
    else if (filter === "unpublished") f = f.filter(l => l.published === 0 || l.published === null || l.published === undefined);
    else if (filter === "active") f = f.filter(l => l.active === 1);
    else if (filter === "inactive") f = f.filter(l => l.active === 0);
    if (search) {
      const q = search.toLowerCase();
      f = f.filter(l => l.code.toLowerCase().includes(q) || l.label.toLowerCase().includes(q));
    }
    return f;
  }, [allLocales, filter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const activeFilter = FILTERS.find(f => f.value === filter);

  async function togglePublished(locale) {
    const newPub = locale.published ? 0 : 1;
    try {
      await apiPatch({ uid: locale.uid, published: newPub, active: newPub || locale.active, edited_by_uid: user?.uid || null });
      toast.success(`Idioma ${newPub ? "publicado" : "despublicado"}`);
      load(); clearLocalesCache(); onChange?.();
    } catch (err) {
      logger.error(err);
      toast.error("Erro ao alterar publicação");
    }
  }

  async function toggleActive(locale) {
    if (locale.code === DEFAULT_LOCALE && locale.active) { toast.warning("Não pode inactivar o idioma Português"); return; }
    const newActive = locale.active ? 0 : 1;
    try {
      await apiPatch({ uid: locale.uid, active: newActive, published: newActive ? locale.published : 0, edited_by_uid: user?.uid || null });
      toast.success(`Idioma ${newActive ? "activado" : "inactivado"}`);
      load(); clearLocalesCache(); onChange?.();
    } catch (err) {
      logger.error(err);
      toast.error("Erro ao alterar estado");
    }
  }

  async function toggleDefault(locale) {
    try {
      await apiPatch({ uid: locale.uid, is_default: 1 });
      toast.success("Idioma padrão alterado");
      load(); clearLocalesCache(); onChange?.();
    } catch (err) {
      logger.error(err);
      toast.error("Erro ao definir idioma padrão");
    }
  }

  async function addLocale() {
    if (!newCode || !newLabel) { toast.warning("Código e nome são obrigatórios"); return; }
    setSaving(true);
    try {
      await apiPost({
        code: newCode.trim().toLowerCase(),
        label: newLabel.trim(),
        flag: newFlag.trim() || "",
        is_default: 0,
        active: 1,
        published: 1,
        sort_order: allLocales.length,
      });
      toast.success("Idioma adicionado");
      setNewCode(""); setNewLabel(""); setNewFlag("");
      setShowForm(false);
      load(); clearLocalesCache(); onChange?.();
    } catch (err) {
      logger.error(err);
      toast.error(err.message || "Erro ao adicionar idioma");
    } finally {
      setSaving(false);
    }
  }

  function confirmRemove(locale) {
    if (locale.code === DEFAULT_LOCALE) { toast.warning("Não pode remover o idioma Português"); return; }
    setConfirm(locale);
  }

  async function softRemove() {
    if (!confirm) return;
    try {
      await apiPatch({ uid: confirm.uid, active: 0, published: 0, deleted_at: new Date().toISOString(), edited_by_uid: user?.uid || null });
      toast.success(`Idioma "${confirm.label}" removido`);
      setConfirm(null);
      load(); clearLocalesCache(); onChange?.();
    } catch (err) {
      logger.error(err);
      toast.error("Erro ao remover idioma");
    }
  }

  function startEdit(l) {
    setEditingUid(l.uid);
    setEditForm({ code: l.code, label: l.label, flag: l.flag || "" });
  }

  function cancelEdit() {
    setEditingUid(null);
    setEditForm({ code: "", label: "", flag: "" });
  }

  async function saveEdit() {
    if (!editForm.code || !editForm.label) { toast.warning("Código e nome são obrigatórios"); return; }
    setEditSaving(true);
    try {
      await apiPatch({ uid: editingUid, code: editForm.code.trim().toLowerCase(), label: editForm.label.trim(), flag: editForm.flag.trim() || "" });
      toast.success("Idioma actualizado");
      cancelEdit();
      load(); clearLocalesCache(); onChange?.();
    } catch (err) {
      logger.error(err);
      toast.error(err.message || "Erro ao actualizar idioma");
    } finally {
      setEditSaving(false);
    }
  }

  // ── Tradução ──
  async function handleCheckLocale() {
    if (!translateModal || !translateSource) return;
    try {
      const res = await checkLocale(translateModal.code, translateSource);
      setCheckResult(res);
      setTranslateStep("review");
    } catch (e) { toast.error("Erro ao verificar estado: " + e.message); }
  }

  async function translateContent(mode, onlyTable, uids) {
    if (!translateModal || !translateSource) return;
    console.log(`[TRANSLATE] → mode:${mode} table:${onlyTable || "all"} uids:${uids ? uids.length : "all"} source:${translateSource} target:${translateModal.code}`);
    setTranslating(true);
    try {
      const body = { source: translateSource, target: translateModal.code, mode };
      if (onlyTable) body.table = onlyTable;
      if (uids && uids.length > 0) body.uids = uids;
      console.log("[TRANSLATE] body:", JSON.stringify(body, null, 2));
      const res = await fetch("/api/translate-content", {
        method: "POST",
        headers: H,
        body: JSON.stringify(body),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error || "Erro na tradução"); }
      const data = await res.json();
      console.log("[TRANSLATE] ← result:", JSON.stringify(data, null, 2));
      clearLocalesCache();
      if (onChange) onChange();
      const updated = await checkLocale(translateModal.code, translateSource);
      setCheckResult(updated);
      toast.success("Operação concluída");
    } catch (e) {
      logger.error(e);
      toast.error("Erro: " + e.message);
    } finally {
      setTranslating(false);
    }
  }

  async function loadDiff(table) {
    if (!translateModal || !translateSource) return;
    try {
      const res = await fetch("/api/translate-diff", {
        method: "POST",
        headers: H,
        body: JSON.stringify({ table, source: translateSource, target: translateModal.code }),
      });
      if (!res.ok) throw new Error("Erro ao carregar detalhes");
      const data = await res.json();
      setDiffData(data);
      // auto-select missing entries
      const sel = {};
      (data.entries || []).forEach(e => { if (!e.translated) sel[e.uid] = true; });
      setSelectedUids(sel);
    } catch (e) {
      logger.error(e);
      toast.error("Erro ao carregar detalhes: " + e.message);
    }
  }

  function toggleUid(uid) {
    setSelectedUids(prev => ({ ...prev, [uid]: !prev[uid] }));
  }

  return (
    <>
      {translateModal && translateStep === "select" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md mx-4 w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center text-champagne"><Globe size={15} /></div>
                <p className="text-sm font-medium text-softblack">Traduzir para {translateModal.label}</p>
              </div>
              <button onClick={() => { setTranslateModal(null); setCheckResult(null); setTranslateStep("select"); }} className="p-1 text-warmgray hover:text-softblack"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Idioma destino</span>
                <p className="text-sm font-medium text-softblack mt-1">{translateModal.flag} {translateModal.label} ({translateModal.code})</p>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Traduzir de <span className="text-red-400">*</span></span>
                <select value={translateSource} onChange={e => setTranslateSource(e.target.value)}
                  className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all">
                  <option value="">— Selecione idioma de origem —</option>
                  {allLocales.filter(l => l.active && l.code !== translateModal.code).map(l => (
                    <option key={l.code} value={l.code}>{l.flag} {l.label} ({l.code})</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => { setTranslateModal(null); setCheckResult(null); setTranslateStep("select"); }} className="px-4 py-2 text-xs font-medium text-warmgray border border-fendi/30 rounded-lg hover:bg-fendi/5">Cancelar</button>
              <button onClick={handleCheckLocale} disabled={!translateSource}
                className="px-4 py-2 text-xs font-medium text-white bg-softblack rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5">
                <Globe size={12} /> Verificar Estado
              </button>
            </div>
          </div>
        </div>
      )}

      {translateModal && translateStep === "review" && checkResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-2xl mx-4 w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center text-champagne"><Globe size={15} /></div>
                <div>
                  <p className="text-sm font-medium text-softblack">Tradução {translateSource ? `(${allLocales.find(l => l.code === translateSource)?.label} → ${translateModal.label})` : ""}</p>
                  <p className="text-[10px] text-warmgray mt-0.5">Gerir conteúdo traduzido por tabela</p>
                </div>
              </div>
              <button onClick={() => { setTranslateModal(null); setCheckResult(null); setTranslateStep("select"); setExpandedTable(null); setDiffData(null); }} className="p-1 text-warmgray hover:text-softblack"><X size={16} /></button>
            </div>

            <div className="space-y-2 max-h-[60vh] overflow-y-auto mb-4">
              {(checkResult._tables || []).map(table => {
                const src = checkResult[`${table}_source`] || 0;
                const tgt = checkResult[`${table}_target`] || 0;
                const cols = checkResult._columns?.[table] || [];
                const missing = Math.max(0, src - tgt);
                const isExpanded = expandedTable === table;
                return (
                  <div key={table} className="border border-fendi/20 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 hover:bg-fendi/5 transition-colors">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${src === 0 ? "bg-green-400" : tgt === 0 ? "bg-red-400" : tgt < src ? "bg-amber-400" : "bg-green-400"}`} />
                        <span className="text-xs font-medium text-softblack capitalize">{table.replace(/_/g, " ")}</span>
                        <span className="text-[10px] text-warmgray/60 hidden sm:inline">{cols.join(", ")}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-[10px] text-warmgray whitespace-nowrap">{src} → {tgt}</span>
                        {tgt > 0 && (
                          <button onClick={() => translateContent("delete", table)} disabled={translating}
                            className="px-2 py-1 text-[9px] font-bold uppercase tracking-wider rounded-md bg-red-50 text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50"
                            title="Eliminar tradução">
                            <Trash2 size={11} />
                          </button>
                        )}
                        <button onClick={() => translateContent("translate", table)} disabled={translating}
                          className="px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider rounded-md bg-champagne/10 text-champagne hover:bg-champagne/20 transition-colors disabled:opacity-50">
                          {tgt > 0 ? "Retrad." : "Trad."}
                        </button>
                        <button onClick={() => { const isExpanded = expandedTable === table; setExpandedTable(isExpanded ? null : table); if (!isExpanded) { setDiffData(null); loadDiff(table); } }}
                          className="p-1 text-warmgray hover:text-softblack transition-colors">
                          <ChevronDown size={12} className={`transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                        </button>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="px-2 py-2 bg-fendi/5 border-t border-fendi/10 space-y-2">
                        {diffData && diffData.table === table ? (
                          <>
                            <div className="max-h-60 overflow-y-auto space-y-1">
                              {(diffData.entries || []).map(entry => (
                                <div key={entry.uid} className={`border border-fendi/20 rounded-lg bg-white ${entry.translated ? "" : "ring-1 ring-amber-200"}`}>
                                  <div className="flex items-start gap-2 p-2">
                                    <input type="checkbox" checked={!!selectedUids[entry.uid]} onChange={() => toggleUid(entry.uid)}
                                      className="accent-softblack shrink-0 mt-0.5" />
                                    <div className="min-w-0 flex-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[10px]">
                                      {(diffData.columns || cols).map(c => (
                                        <div key={c} className="contents">
                                          <span className="text-warmgray/60 uppercase tracking-wider text-[8px] font-medium pt-0.5">{c}</span>
                                          <div className="min-w-0">
                                            <span className="text-softblack break-words">{entry[c] || ""}</span>
                                            {entry[`${c}_tgt`] !== undefined && entry[`${c}_tgt`] !== entry[c] && (
                                              <span className="text-green-600 block text-[9px] leading-tight mt-0.5 truncate" title={entry[`${c}_tgt`]}>→ {entry[`${c}_tgt`]}</span>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                      <span className="text-warmgray/60 uppercase tracking-wider text-[8px] font-medium pt-0.5">UID</span>
                                      <span className="text-[8px] text-warmgray/40 truncate">{entry.uid}</span>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0 mt-0.5">
                                      <span className={`px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider rounded ${entry.translated ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
                                        {entry.translated ? "Trad." : "Pend."}
                                      </span>
                                      <button onClick={e => { e.preventDefault(); e.stopPropagation(); translateContent("translate", diffData.table, [entry.uid]); }} disabled={translating}
                                        className="px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider rounded-md bg-champagne/10 text-champagne hover:bg-champagne/20 transition-colors disabled:opacity-50"
                                        title={entry.translated ? "Retraduzir este" : "Traduzir este"}>
                                        {entry.translated ? "Retrad." : "Trad."}
                                      </button>
                                      <button onClick={e => { e.preventDefault(); e.stopPropagation(); translateContent("delete", diffData.table, [entry.uid]); }} disabled={translating}
                                        className="p-0.5 text-red-400 hover:text-red-600 transition-colors disabled:opacity-50"
                                        title="Eliminar este">
                                        <Trash2 size={10} />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="flex gap-2 pt-1 flex-wrap">
                              {tgt > 0 && (
                                <button onClick={() => translateContent("delete", table)} disabled={translating}
                                  className="px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider rounded-md bg-red-50 text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50 flex items-center gap-1">
                                  <Trash2 size={10} /> Eliminar tudo
                                </button>
                              )}
                              <button onClick={() => translateContent("translate", table)} disabled={translating}
                                className="px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider rounded-md bg-champagne/10 text-champagne hover:bg-champagne/20 transition-colors disabled:opacity-50">
                                {tgt > 0 ? "Retraduzir tudo" : "Traduzir tudo"}
                              </button>
                              {Object.values(selectedUids).filter(Boolean).length > 0 && (
                                <button onClick={() => {
                                  const uids = Object.entries(selectedUids).filter(([,v]) => v).map(([k]) => k);
                                  translateContent("translate", table, uids);
                                }} disabled={translating}
                                  className="px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider rounded-md bg-green-50 text-green-700 hover:bg-green-100 transition-colors disabled:opacity-50">
                                  Trad. {Object.values(selectedUids).filter(Boolean).length} selec.
                                </button>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="flex items-center justify-center gap-2 text-[10px] text-warmgray py-4">
                            <Loader2 size={12} className="animate-spin" /> A carregar detalhes...
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end gap-2 border-t border-fendi/10 pt-4">
              <button onClick={() => { setTranslateModal(null); setCheckResult(null); setTranslateStep("select"); setExpandedTable(null); setDiffData(null); }} className="px-4 py-2 text-xs font-medium text-warmgray border border-fendi/30 rounded-lg hover:bg-fendi/5">Fechar</button>
              {!translating && (
                <button onClick={() => translateContent("translate")}
                  className="px-4 py-2 text-xs font-medium text-white bg-softblack rounded-lg hover:opacity-90 flex items-center gap-1.5">
                  <Globe size={12} /> Traduzir Tudo
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {translating && (
        <TranslateOverlay />
      )}
      <div className="space-y-6">

      {editingUid && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={cancelEdit}>
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg mx-4 w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center text-champagne"><Pencil size={15} /></div>
                <p className="text-sm font-medium text-softblack">Editar Idioma</p>
              </div>
              <button onClick={cancelEdit} className="p-1 text-warmgray hover:text-softblack"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <div className="space-y-4">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Código <span className="text-red-400">*</span></span>
                  <input value={editForm.code} onChange={e => setEditForm({ ...editForm, code: e.target.value })} placeholder={PLH_CODE} className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Nome</span>
                  <input value={editForm.label} onChange={e => setEditForm({ ...editForm, label: e.target.value })} placeholder={PLH_LANG_NAME} className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Bandeira</span>
                  <input value={editForm.flag} onChange={e => setEditForm({ ...editForm, flag: e.target.value })} placeholder={PLH_FLAG} className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" />
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={cancelEdit} className="px-4 py-2 text-xs font-medium text-warmgray border border-fendi/30 rounded-lg hover:bg-fendi/5">Cancelar</button>
              <button onClick={saveEdit} disabled={editSaving} className="px-4 py-2 text-xs font-medium text-white bg-softblack rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5">
                <Save size={12} /> {editSaving ? "A guardar..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={() => setConfirm(null)}>
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm mx-4 w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-red-50 shrink-0"><AlertTriangle size={18} className="text-red-500" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-softblack">Remover idioma</p>
                <p className="text-xs text-warmgray mt-1">Tem a certeza que deseja remover <strong>{confirm.label}</strong>?</p>
              </div>
              <button onClick={() => setConfirm(null)} className="p-1 text-warmgray hover:text-softblack shrink-0"><X size={14} /></button>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setConfirm(null)} className="px-4 py-2 text-xs font-medium text-warmgray border border-fendi/30 rounded-lg hover:bg-fendi/5">Cancelar</button>
              <button onClick={softRemove} className="px-4 py-2 text-xs font-medium text-white bg-red-600 rounded-lg hover:opacity-90 transition-opacity">Remover</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-champagne/10 flex items-center justify-center text-champagne shrink-0">
            <Languages size={16} />
          </div>
          <div>
            <h3 className="text-base font-medium text-softblack">Idiomas</h3>
            <p className="text-xs text-warmgray mt-0.5">{allLocales.length} idiomas configurados</p>
          </div>
        </div>
        <button onClick={() => { setShowForm(!showForm); if (!showForm) { setNewCode(""); setNewLabel(""); setNewFlag(""); } }} className="btn-primary !py-2 !px-4 !text-[10px] flex items-center gap-2 shrink-0 self-start">
          <Plus size={14} /> Novo Idioma
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg mx-4 w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center text-champagne"><Plus size={15} /></div>
                <p className="text-sm font-medium text-softblack">Novo Idioma</p>
              </div>
              <button onClick={() => setShowForm(false)} className="p-1 text-warmgray hover:text-softblack"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Código <span className="text-red-400">*</span></span>
                <input value={newCode} onChange={e => setNewCode(e.target.value)} placeholder={PLH_CODE} className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Nome</span>
                <input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder={PLH_LANG_NAME} className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Bandeira</span>
                <input value={newFlag} onChange={e => setNewFlag(e.target.value)} placeholder={PLH_FLAG} className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" />
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-xs font-medium text-warmgray border border-fendi/30 rounded-lg hover:bg-fendi/5">Cancelar</button>
              <button onClick={addLocale} disabled={saving} className="px-4 py-2 text-xs font-medium text-white bg-softblack rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5">
                <Save size={12} /> {saving ? "A adicionar..." : "Adicionar"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div data-id="admin-locales" className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-warmgray/60" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={PLH_SEARCH_LOCALE} className="w-full pl-9 pr-3 py-2 text-xs border border-fendi/30 rounded-lg bg-white focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" />
        </div>
        <div className="relative">
          <button onClick={() => setFilterOpen(!filterOpen)} className="w-full sm:w-auto px-4 py-2 text-xs border border-fendi/30 rounded-lg bg-white flex items-center gap-2 hover:border-fendi/60 transition-colors">
            {activeFilter?.label || "Filtrar"} <ChevronDown size={12} className={`transition-transform ${filterOpen ? "rotate-180" : ""}`} />
          </button>
          {filterOpen && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-fendi/30 rounded-lg shadow-lg py-1 min-w-[140px] z-20" onMouseLeave={() => setFilterOpen(false)}>
              {FILTERS.map(f => (
                <button key={f.value} onClick={() => { setFilter(f.value); setFilterOpen(false); }}
                  className={`block w-full text-left px-4 py-2 text-xs transition-colors ${filter === f.value ? "text-champagne bg-champagne/5 font-medium" : "text-warmgray hover:text-softblack hover:bg-fendi/10"}`}>
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {paged.length === 0 ? (
        <div className="bg-white border border-fendi/40 rounded-xl p-10 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-fendi/10 flex items-center justify-center mb-4">
            <Search size={20} className="text-fendi/40" />
          </div>
          <p className="text-sm text-warmgray">{EMPTY_LOCALES}</p>
        </div>
      ) : (
        <div className="bg-white border border-fendi/40 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-fendi/20 bg-fendi/5">
                  <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-warmgray font-medium text-left">Código</th>
                  <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-warmgray font-medium text-left">Nome</th>
                  <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-warmgray font-medium text-left">Bandeira</th>
                  <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-warmgray font-medium text-left">Publicado</th>
                  <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-warmgray font-medium text-left">Activo</th>
                  <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-warmgray font-medium text-left">Padrão</th>
                  <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-warmgray font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((l) => (
                  <tr key={l.code} className={`border-b border-fendi/10 hover:bg-fendi/5 transition-colors ${!l.active ? "opacity-50" : ""}`}>
                    <td className="px-5 py-4">
                      <span className="font-mono text-xs text-softblack">{l.code}</span>
                    </td>
                    <td className="px-5 py-4 text-sm text-softblack font-medium">{l.label}</td>
                    <td className="px-5 py-4 text-lg">{l.flag}</td>
                    <td className="px-5 py-4">
                      <button onClick={() => togglePublished(l)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider transition-colors ${l.published ? "bg-green-50 text-green-700 hover:bg-green-100" : "bg-gray-100 text-warmgray hover:bg-gray-200"}`}
                        title={l.published ? "Clique para despublicar" : "Clique para publicar"}>
                        {l.published ? <Eye size={11} /> : <EyeOff size={11} />}
                        {l.published ? "Sim" : "Não"}
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      <button onClick={() => { if (l.code === DEFAULT_LOCALE && l.active) { toast.warning("Não pode inactivar Português"); return; } toggleActive(l); }}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider transition-colors ${l.active ? "bg-blue-50 text-blue-700 hover:bg-blue-100" : "bg-red-50 text-red-700 hover:bg-red-100"}`}>
                        {l.active ? <Check size={11} /> : <X size={11} />}
                        {l.active ? "Sim" : "Não"}
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      {l.is_default ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-champagne/10 text-champagne">Padrão</span>
                      ) : (
                        <button onClick={() => toggleDefault(l)} className="text-[10px] text-warmgray hover:text-softblack underline underline-offset-2">Definir</button>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => startEdit(l)} className="p-1.5 rounded-lg text-warmgray hover:text-champagne hover:bg-champagne/5 transition-colors" title={TIP_EDIT}>
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => { setTranslateModal(l); setTranslateSource(""); }} disabled={l.code === DEFAULT_LOCALE || allLocales.filter(x => x.active && x.code !== l.code).length === 0}
                          className={`p-1.5 rounded-lg transition-colors ${l.code === DEFAULT_LOCALE || allLocales.filter(x => x.active && x.code !== l.code).length === 0 ? "text-fendi/30 cursor-not-allowed" : "text-warmgray hover:text-champagne hover:bg-champagne/5"}`} title={TIP_TRANSLATE}>
                          <Globe size={14} />
                        </button>
                        <button onClick={() => confirmRemove(l)} disabled={l.code === DEFAULT_LOCALE} className={`p-1.5 rounded-lg transition-colors ${l.code === DEFAULT_LOCALE ? "text-fendi/30 cursor-not-allowed" : "text-warmgray hover:text-red-600 hover:bg-red-50"}`} title={TIP_REMOVE}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="bg-white border border-fendi/40 rounded-xl p-3 md:p-4 flex flex-col sm:flex-row items-center justify-between gap-3 md:gap-4">
          <div className="flex items-center gap-3">
            <p className="text-xs md:text-sm text-warmgray">
              {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filtered.length)} de {filtered.length}
            </p>
            <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
              className="text-xs border border-fendi/30 rounded-lg px-2 py-1.5 bg-white focus:border-champagne outline-none">
              {PAGE_SIZES.map(s => <option key={s} value={s}>{s} / pág</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1 md:gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1}
              className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center border border-fendi/30 rounded-lg hover:bg-fendi/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) pageNum = i + 1;
              else if (safePage <= 3) pageNum = i + 1;
              else if (safePage >= totalPages - 2) pageNum = totalPages - 4 + i;
              else pageNum = safePage - 2 + i;
              return (
                <button key={pageNum} onClick={() => setPage(pageNum)}
                  className={`w-7 h-7 md:w-9 md:h-9 flex items-center justify-center rounded-lg text-[11px] md:text-sm transition-colors ${safePage === pageNum ? "bg-softblack text-white" : "border border-fendi/30 hover:bg-fendi/20"}`}>
                  {pageNum}
                </button>
              );
            })}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}
              className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center border border-fendi/30 rounded-lg hover:bg-fendi/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

function TranslateOverlay() {
  const [msg, setMsg] = useState(TRANSLATE_MESSAGES[0]);
  const idxRef = useRef(0);
  useEffect(() => {
    const id = setInterval(() => {
      idxRef.current = (idxRef.current + 1) % TRANSLATE_MESSAGES.length;
      setMsg(TRANSLATE_MESSAGES[idxRef.current]);
    }, 5000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl p-8 max-w-sm mx-4 w-full text-center" onClick={e => e.stopPropagation()}>
        <div className="text-6xl mb-4 animate-bounce">👨‍💻</div>
        <p className="text-sm font-medium text-softblack">{msg}</p>
      </div>
    </div>
  );
}
